import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Mic, Loader2, ArrowRight, Circle, Square,
  CheckCircle2, AlertTriangle, Lock, Zap, Sparkles,
  Volume2, RefreshCw, Users, User, Briefcase, Cpu, Crown, Search, Trophy, Check,
} from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { ReportPanel } from "@/pages/VideoMockInterview";
import PresetJobPicker from "@/components/PresetJobPicker";
import FreeSummaryScreen from "@/components/FreeSummaryScreen";
import UpgradeLiveModal from "@/components/UpgradeLiveModal";
import SampleShareCard from "@/components/SampleShareCard";
import InterviewAvatar from "@/components/InterviewAvatar";
import { useEntitlement } from "@/lib/entitlement-context";
import { useAuth } from "@/lib/auth-context";
import GuestGate from "@/components/GuestGate";
import { useWorkspace } from "@/lib/workspace-context";
import { WorkspaceMismatchBanner, WorkspaceMismatchDialog } from "@/components/WorkspaceMismatch";
import { ActiveSessionDialog } from "@/components/ActiveSessionDialog";
import { MicLevelMeter, UploadStages, permissionErrorMessage } from "@/components/RecorderKit";
import DeliverableFeedback from "@/components/DeliverableFeedback";

const PERSONA_ICONS = {
  hr_officer: User,
  hiring_manager: Briefcase,
  executive: Crown,
  recruiter: Search,
  tech_lead: Cpu,
  panel: Users,
};

/* -------------------------- Locked preview -------------------------- */

function LockedPreview() {
  return (
    <div data-testid="live-locked" className="max-w-4xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">04 · Official AI Interview · Premium</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Official AI Interview</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Put your preparation to the test. A realistic AI interviewer speaks naturally, listens to
          your spoken answers, and adapts with intelligent follow-ups, complete the assessment to
          receive your Official Readiness Score.
        </p>
      </div>

      <div className="relative rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/50 to-zinc-950/70 p-8 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className="h-32 w-40 rounded-xl border border-zinc-800 bg-zinc-950/60 flex items-center justify-center relative overflow-hidden">
            <Lock className="h-8 w-8 text-amber-400" />
            <div className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest text-zinc-600">Locked</div>
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/25 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
              <Sparkles className="h-3 w-3" /> Premium feature
            </div>
            <h2 className="mt-3 text-xl md:text-2xl font-bold text-zinc-50">
              Voice-first practice with a real interviewer persona.
            </h2>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-300">
              {[
                "6 interviewer personas",
                "AI speaks, you speak",
                "Real follow-up questions",
                "Executive Interview Report",
                "AI Interview Replay",
                "Auto Interview Day Brief",
                "Updated Readiness Score",
                "Track attempts over time",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/app/billing"
                data-testid="live-locked-unlock"
                className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)]"
              >
                <Zap className="h-3.5 w-3.5" />
                Try Interview Ready — free during beta
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-sm border border-zinc-800 text-zinc-100 hover:bg-zinc-900 rounded-lg px-4 py-2.5"
              >
                Compare plans <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Persona picker -------------------------- */

function PersonaPicker({ personas, value, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
      {personas.map((p) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            data-testid={`live-persona-${p.id}`}
            className={`text-left p-4 rounded-lg border transition-all ${
              active
                ? "border-amber-500/50 bg-amber-500/[0.06] shadow-[0_0_20px_-8px_rgba(245,158,11,0.4)]"
                : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`h-11 w-11 rounded-full overflow-hidden flex-shrink-0 ring-2 ${
                  active ? "ring-amber-500/60" : "ring-zinc-800"
                }`}
              >
                <img
                  src={`/avatars/${p.id}_base.webp`}
                  alt=""
                  className="h-full w-full object-cover"
                  style={{ objectPosition: "center 18%" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  draggable={false}
                />
              </span>
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${active ? "text-amber-300" : "text-zinc-100"}`}>{p.name}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{p.tagline}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------- Setup step -------------------------- */

function GuestConversionPanel() {
  return (
    <div data-testid="guest-conversion-panel" className="max-w-3xl mx-auto mt-4 rounded-2xl border border-amber-500/25 bg-amber-500/[0.05] p-6 sm:p-8 text-center">
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3">
        <Sparkles className="h-3 w-3" /> That was the sample
      </div>
      <h2 className="text-base md:text-lg font-semibold text-zinc-50">
        Now imagine this with your own resume and your own job description.
      </h2>
      <p className="mt-2 text-sm text-zinc-400 max-w-lg mx-auto leading-relaxed">
        Create a free account to upload your resume, paste the job you're chasing, and get
        personalized predicted questions, an ATS score, and your own Interview Readiness Score.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-3">
        <Link to="/register" data-testid="guest-signup-cta"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-6 py-3 text-sm transition-colors">
          Test Your Own Application <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/login" data-testid="guest-login-cta"
              className="inline-flex items-center rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 px-6 py-3 text-sm transition-colors">
          I already have an account
        </Link>
      </div>
    </div>
  );
}

function SetupPanel({ personas, onStart, isPaid }) {
  const { user: authUser } = useAuth();
  const isGuestUser = !!authUser?.is_guest;
  const [personaId, setPersonaId] = useState(personas[1]?.id || "hiring_manager");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [jd, setJd] = useState("");
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resume, setResume] = useState(null);
  const [mismatchDetail, setMismatchDetail] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const sampleId = searchParams.get("sample");
  const [samplePack, setSamplePack] = useState(null);
  useEffect(() => {
    if (!sampleId) { setSamplePack(null); return; }
    api.get(`/samples/${sampleId}`).then(({ data }) => setSamplePack(data)).catch(() => setSamplePack(null));
  }, [sampleId]);
  const clearSample = () => {
    searchParams.delete("sample");
    setSearchParams(searchParams, { replace: true });
  };
  const [mismatchDismissed, setMismatchDismissed] = useState(false);
  const [activeDetail, setActiveDetail] = useState(null);
  const { setActive } = useWorkspace();

  const _run = async (confirmMismatch, replaceActive) => {
    setBusy(true);
    try {
      const { data } = await api.post("/video-interview/start", {
        job_title: role || null,
        company: company || null,
        job_description: sampleId ? "" : jd,
        total_questions: count,
        persona: personaId,
        mode: "live",
        resume_id: sampleId ? null : (resume?.resume_id || null),
        sample_id: sampleId || null,
        confirm_workspace_mismatch: !!confirmMismatch,
        replace_active: !!replaceActive,
      });
      setMismatchDetail(null);
      setActiveDetail(null);
      onStart(data);
    } catch (e) {
      if (
        e?.response?.status === 409 &&
        e?.response?.data?.detail?.error === "workspace_mismatch"
      ) {
        setMismatchDetail(e.response.data.detail);
      } else if (
        e?.response?.status === 409 &&
        e?.response?.data?.detail?.error === "active_session"
      ) {
        setActiveDetail(e.response.data.detail);
      } else {
        setErr(formatApiError(e?.response?.data?.detail) || "Could not start the interview.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resumeActive = async () => {
    if (!activeDetail?.session_id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/video-interview/${activeDetail.session_id}`);
      setActiveDetail(null);
      onStart(data);
    } catch {
      setErr("Could not resume the interview, please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setErr("");
    if (!sampleId && jd.trim().length < 40) {
      setErr("Paste a job description (at least 40 characters).");
      return;
    }
    await _run(false);
  };

  if (isGuestUser && !sampleId) {
    return <GuestGate feature="run an official AI interview with your own resume and job description" />;
  }

  return (
    <div data-testid="live-setup" className="max-w-3xl xl:max-w-6xl space-y-6">
      {sampleId && (
        <div data-testid="sample-mode-banner" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">Sample Mode</div>
            <div className="text-sm text-zinc-100 mt-0.5">
              You're trying Intuiterview with a sample candidate, pick an interviewer and press Start. This won't use your free interview.
            </div>
          </div>
          <button onClick={clearSample} data-testid="sample-exit-btn" className="text-xs text-zinc-400 hover:text-zinc-200 underline underline-offset-2">
            Exit sample mode
          </button>
        </div>
      )}
      <ActiveSessionDialog
        detail={activeDetail}
        busy={busy}
        onResume={resumeActive}
        onStartOver={() => _run(true, true)}
        onCancel={() => setActiveDetail(null)}
      />
      <WorkspaceMismatchDialog
        detail={mismatchDetail}
        jd={jd}
        onSwitch={() => {
          if (mismatchDetail?.suggested_workspace_id) {
            setActive(mismatchDetail.suggested_workspace_id);
            setMismatchDetail(null);
            setTimeout(() => _run(false), 100);
          }
        }}
        onCreated={() => {
          setMismatchDetail(null);
          setTimeout(() => _run(false), 150);
        }}
        onContinue={() => {
          setMismatchDetail(null);
          _run(true);
        }}
        onCancel={() => setMismatchDetail(null)}
      />
      <WorkspaceMismatchBanner
        role={role}
        company={company}
        jd={jd}
        dismissed={mismatchDismissed}
        onDismiss={() => setMismatchDismissed(true)}
      />
      {/* You're about to earn, outcome anchor */}
      <div
        data-testid="live-outcome-ribbon"
        className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/40 to-zinc-950/60 p-4 sm:p-5"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full border border-amber-500/40 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
            <Trophy className="h-4 w-4 text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">
              You&apos;re about to earn
            </div>
            <div className="text-sm text-zinc-100 font-semibold mt-0.5">
              Your Official Interview Readiness Assessment
            </div>
            <ul className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-zinc-300">
              {[
                "Overall Readiness Score",
                "Hiring Confidence Level",
                "Strength Analysis",
                "Improvement Areas",
                "Interview Report",
              ].map((f) => (
                <li key={f} className="flex items-start gap-1.5">
                  <Check className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Choose your interviewer</div>
        <PersonaPicker personas={personas} value={personaId} onChange={setPersonaId} />
      </div>

      {sampleId ? (
        <div data-testid="sample-pack-card" className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your sample interview</div>
          {samplePack ? (
            <>
              <div className="text-sm font-semibold text-zinc-100">
                {samplePack.candidate}, {samplePack.role}
                {samplePack.company ? <span className="text-zinc-400 font-normal"> at {samplePack.company}</span> : null}
              </div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{samplePack.background}</div>
            </>
          ) : (
            <div className="text-xs text-zinc-400">Loading sample candidate…</div>
          )}
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-zinc-300">
            {["Sample resume included", "Sample job description included", "2 questions"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 border border-zinc-800 bg-zinc-950 rounded-full px-2.5 py-1">
                <Check className="h-3 w-3 text-amber-400" strokeWidth={3} /> {t}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">
            The role, résumé and job description are set for you, just choose your interviewer above and press Start.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PresetJobPicker
              role={role}
              setRole={setRole}
              company={company}
              setCompany={setCompany}
              jd={jd}
              setJd={setJd}
              onResumeChange={setResume}
              testidPrefix="live"
              jdRows={7}
              className="sm:col-span-2"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Interview length</div>
            <div data-testid="live-length-picker" className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { n: 1, name: "Quick Try", desc: "1 core question" },
                { n: 3, name: "Quick Interview", desc: "3 core questions" },
                { n: 5, name: "Standard Interview", desc: "5 core questions" },
                { n: 10, name: "Comprehensive Interview", desc: "10 core questions" },
              ].map((opt) => (
                <button
                  key={opt.n}
                  type="button"
                  onClick={() => setCount(opt.n)}
                  data-testid={`live-length-${opt.n}`}
                  className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
                    count === opt.n
                      ? "border-amber-500/60 bg-amber-500/10"
                      : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                  }`}
                >
                  <div className={`text-sm font-semibold ${count === opt.n ? "text-amber-200" : "text-zinc-100"}`}>
                    {opt.name}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
            <div data-testid="live-length-note" className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
              The AI selects your questions from the 15-question bank predicted for this role, keeping
              competency coverage. It may ask a short follow-up when an answer needs more depth —
              follow-ups never count toward your question total.
            </div>
          </div>
        </>
      )}
      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
          <div>
            {err}
            {isGuestUser && (
              <div className="mt-1.5">
                <Link to="/register" data-testid="guest-limit-signup" className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 font-semibold">
                  Create your free account <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="pt-1">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="live-start-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 text-sm shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
          {busy ? "Preparing your interview…" : "Start live interview"}
        </button>
      </div>
      <div className="text-[11px] text-zinc-600 leading-relaxed pt-1">
        We&apos;ll ask for microphone (and optional camera) access. Answers are recorded to your account only — delete them anytime from Settings &rarr; Privacy.
      </div>
    </div>
  );
}

/* -------------------------- Voice-first recorder -------------------------- */

function LiveRecorder({ session, onSession, onFinalize }) {
  const [stream, setStream] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [permissionErr, setPermissionErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [enableVideo, setEnableVideo] = useState(true);
  const [upPhase, setUpPhase] = useState(null);
  const [upPct, setUpPct] = useState(0);
  const [failedUpload, setFailedUpload] = useState(null);
  const [uploadErr, setUploadErr] = useState("");
  const [recErr, setRecErr] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const evalTimerRef = useRef(null);
  const streamRef = useRef(null);
  const wantVideoRef = useRef(true);

  const currentTurn = (session.turns || []).find((t) => t.status === "asked");
  const answered = (session.turns || []).filter((t) => t.status === "answered").length;
  const answeredPrimary = (session.turns || []).filter((t) => t.status === "answered" && !t.is_followup).length;
  const total = session.total_questions || 5;
  const questionNumber = currentTurn?.primary_index != null ? currentTurn.primary_index + 1 : Math.min(answered + 1, total);
  const done = !currentTurn;

  const startCamera = useCallback(async (withVideo) => {
    setPermissionErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: withVideo ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
        audio: true,
      });
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = s;
      setStream(s);
    } catch (e) {
      setPermissionErr(permissionErrorMessage(e, withVideo ? "camera and microphone" : "microphone"));
    }
  }, []);

  // Camera OFF must actually stop video capture — not just hide the preview —
  // so a recording made while the camera is off contains no video track.
  const toggleVideo = async (on) => {
    setEnableVideo(on);
    wantVideoRef.current = on;
    if (!on) {
      const s = streamRef.current;
      if (s) s.getVideoTracks().forEach((t) => { t.stop(); s.removeTrack(t); });
      if (videoRef.current) videoRef.current.srcObject = null;
    } else {
      await startCamera(true);
      // user may have toggled off again while the camera was re-acquiring
      if (!wantVideoRef.current && streamRef.current) {
        streamRef.current.getVideoTracks().forEach((t) => { t.stop(); streamRef.current.removeTrack(t); });
      }
    }
  };

  useEffect(() => {
    startCamera(true);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enableVideo && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, enableVideo]);

  useEffect(() => {
    const warn = (e) => {
      if (recording || uploading) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [recording, uploading]);

  // Auto-play the current question via TTS whenever it changes, and fetch its
  // precomputed viseme timeline (built server-side in the background) so the
  // avatar speaks with phoneme-accurate mouth shapes. Playback never waits.
  const [visemes, setVisemes] = useState(null);
  const [mouthGated, setMouthGated] = useState(false);
  useEffect(() => {
    if (!currentTurn || done) return;
    const url = `${API_BASE}/video-interview/${session.session_id}/turn/${currentTurn.index}/tts`;
    setVisemes(null);
    setMouthGated(true);
    let alive = true;
    let started = false;
    const startAudio = () => {
      if (!alive || started) return;
      started = true;
      if (audioRef.current) {
        audioRef.current.src = url;
        setTtsPlaying(true);
        audioRef.current.play().catch(() => setTtsPlaying(false));
      }
    };
    // The timeline is prewarmed server-side at turn creation, so the first
    // poll usually returns "ready" and playback starts with precise word-level
    // lip sync from the very first second.
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/video-interview/${session.session_id}/turn/${currentTurn.index}/visemes`);
        if (!alive) return;
        if (data.status === "ready" && Array.isArray(data.visemes)) {
          setVisemes(data.visemes);
          setMouthGated(false);
          startAudio();
          return;
        }
      } catch { /* keep polling */ }
      if (alive && ++tries < 14) setTimeout(poll, tries < 5 ? 300 : 1000);
    };
    poll();
    // Never hold the interview hostage: if the timeline isn't ready shortly,
    // play anyway with the mouth gated (subtle motion) until it arrives.
    const playFallback = setTimeout(startAudio, 1500);
    // And never gate forever — if the timeline fails, restore spectral sync.
    const ungate = setTimeout(() => { if (alive) setMouthGated(false); }, 5000);
    return () => { alive = false; clearTimeout(playFallback); clearTimeout(ungate); };
  }, [currentTurn?.index, done, session.session_id]);

  const startRecording = () => {
    if (!stream || !currentTurn) return;
    setRecErr("");
    setUploadErr("");
    chunksRef.current = [];
    const hasVideo = stream.getVideoTracks && stream.getVideoTracks().length > 0;
    const candidates = hasVideo
      ? [
          "video/webm;codecs=vp9,opus",
          "video/webm;codecs=vp8,opus",
          "video/webm",
          "video/mp4;codecs=avc1,mp4a.40.2", // Safari
          "video/mp4",
        ]
      : [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/ogg;codecs=opus",
          "audio/mp4;codecs=mp4a.40.2", // Safari
          "audio/mp4",
        ];
    const mime = candidates.find((m) => {
      try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
    }) || "";
    // SPEED: cap bitrate - browser default (~2.5 Mbps video) doubled upload size
    // for no visible quality gain on webcam answers.
    const bitrateOpts = hasVideo
      ? { videoBitsPerSecond: 1000000, audioBitsPerSecond: 96000 }
      : { audioBitsPerSecond: 96000 };
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime, ...bitrateOpts } : bitrateOpts);
    } catch (e) {
      setRecErr(
        "Your browser can't record this interview. Please try the latest Chrome, Edge, or Safari."
      );
      return;
    }
    rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => submitAnswer(mime);
    rec.onerror = (e) => {
      console.error("MediaRecorder error", e);
      setRecErr("Recording failed unexpectedly. Please try recording again.");
    };
    try {
      rec.start(1000);
    } catch (e) {
      setRecErr(
        "Couldn't start the recorder. Try refreshing the page, then reallow camera & mic permissions."
      );
      return;
    }
    setRecorder(rec);
    setRecording(true);
    setElapsed(0);
    elapsedRef.current = 0;
    timerRef.current = setInterval(() => setElapsed((s) => { elapsedRef.current = s + 1; return s + 1; }), 1000);
  };

  const stopRecording = () => {
    if (recorder && recording) {
      recorder.stop();
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecording(false);
    }
  };

  const doUpload = async (payload) => {
    if (!payload) return;
    setUploading(true);
    setUploadErr("");
    setUpPhase("uploading");
    setUpPct(0);
    try {
      const fd = new FormData();
      fd.append("video", payload.blob, `turn-${payload.turnIndex}.${payload.ext}`);
      fd.append("turn_index", String(payload.turnIndex));
      const { data } = await api.post(
        `/video-interview/${session.session_id}/answer`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000,
          onUploadProgress: (ev) => {
            const p = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setUpPct(p);
            if (p >= 100) {
              setUpPhase("transcribing");
              if (evalTimerRef.current) clearTimeout(evalTimerRef.current);
              // display heuristic: transcription time tracks answer length, evaluation follows
              evalTimerRef.current = setTimeout(
                () => setUpPhase("evaluating"),
                Math.min(20000, 2500 + (payload.duration || 30) * 250),
              );
            }
          },
        },
      );
      setFailedUpload(null);
      onSession(data.session);
    } catch (e) {
      setFailedUpload(payload);
      setUploadErr(e?.response?.data?.detail ? formatApiError(e.response.data.detail) : "Upload failed — check your connection and retry. Your recording is saved.");
    } finally {
      if (evalTimerRef.current) { clearTimeout(evalTimerRef.current); evalTimerRef.current = null; }
      setUploading(false);
      setUpPhase(null);
    }
  };

  const submitAnswer = async (mime) => {
    if (!currentTurn) return;
    const isAudio = mime && mime.startsWith("audio/");
    const isMp4 = mime && mime.includes("mp4");
    const ext = isMp4 ? (isAudio ? "m4a" : "mp4") : "webm";
    const blobType = mime ? mime.split(";")[0] : (isAudio ? "audio/webm" : "video/webm");
    const blob = new Blob(chunksRef.current, { type: blobType });
    await doUpload({ blob, ext, turnIndex: currentTurn.index, duration: elapsedRef.current });
  };

  const finalize = async () => {
    setFinalizing(true);
    setUploadErr("");
    try {
      const { data } = await api.post(`/video-interview/${session.session_id}/finalize`);
      onFinalize(data);
    } catch (e) {
      setUploadErr(formatApiError(e?.response?.data?.detail) || "Could not finalize the interview. Please try again.");
    } finally {
      setFinalizing(false);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, "0")}`;

  return (
    <div data-testid="live-recorder" className="max-w-5xl space-y-5">
      {/* Interview room, AI interviewer + candidate, like a real video call */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/80 p-3 sm:p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-3">
          <InterviewAvatar
            personaId={session.persona}
            speaking={ttsPlaying}
            listening={recording}
            audioRef={audioRef}
            visemes={visemes}
            mouthGated={mouthGated}
          />
          <div className="relative rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden min-h-[220px] md:min-h-0">
            {enableVideo ? (
              <video ref={videoRef} playsInline className="w-full h-full object-cover" data-testid="live-preview" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                <Mic className="h-8 w-8 mb-2" />
                <div className="text-xs">Audio-only mode</div>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-zinc-950/85 to-transparent pointer-events-none" />
            <div className="absolute left-3 bottom-2 text-[11px] font-medium text-zinc-300">You</div>
            <div className="absolute right-2 bottom-2"><MicLevelMeter stream={stream} /></div>
            {recording && (
              <div
                data-testid="rec-chip"
                className={`absolute top-3 left-3 inline-flex items-center gap-2 text-xs text-white rounded-full px-3 py-1 font-medium transition-colors ${
                  elapsed >= 300
                    ? "bg-amber-500/95 shadow-[0_0_20px_-4px_rgba(245,158,11,0.85)]"
                    : elapsed >= 240
                    ? "bg-amber-500/85"
                    : "bg-rose-500/90"
                }`}
              >
                <Circle className="h-2 w-2 fill-white animate-pulse" /> REC · {fmt(elapsed)}
                {elapsed >= 300 && (
                  <span data-testid="rec-wrap-up" className="ml-1 text-[10px] uppercase tracking-widest opacity-95">
                    wrap up soon
                  </span>
                )}
              </div>
            )}
            {!stream && !permissionErr && (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Requesting devices…
              </div>
            )}
            {permissionErr && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 text-sm p-6 text-center bg-zinc-950/90">
                <Mic className="h-8 w-8 text-zinc-600 mb-3" />
                <div>{permissionErr}</div>
                <button
                  onClick={() => startCamera(enableVideo)}
                  data-testid="live-retry-perm"
                  className="mt-4 text-xs border border-zinc-800 rounded-md px-3 py-1.5 hover:bg-zinc-900"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current question, big card */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] via-zinc-900/50 to-zinc-950/70 p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <span className="h-10 w-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Volume2 className={`h-4 w-4 ${ttsPlaying ? "text-amber-400 animate-pulse" : "text-amber-400"}`} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-2">
              Question {questionNumber} of {total}
              {currentTurn?.is_followup && (
                <span className="text-[9px] uppercase tracking-widest text-sky-300 border border-sky-500/30 bg-sky-500/[0.06] rounded-full px-1.5 py-0.5">
                  Follow-up
                </span>
              )}
            </div>
            <div className="mt-2 text-lg text-zinc-100 leading-relaxed" data-testid="live-current-question">
              {currentTurn?.question || "All questions answered, click Generate Executive Report."}
            </div>
          </div>
        </div>
        <audio
          ref={audioRef}
          onPlaying={() => setTtsPlaying(true)}
          onPause={() => setTtsPlaying(false)}
          onEnded={() => setTtsPlaying(false)}
          onError={() => setTtsPlaying(false)}
          className="hidden"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {!done && !recording && !uploading && !failedUpload && (
              <button
                onClick={startRecording}
                disabled={!stream}
                data-testid="live-record-btn"
                className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-60"
              >
                <Circle className="h-3 w-3 fill-white" /> Start speaking
              </button>
            )}
            {recording && (
              <button
                onClick={stopRecording}
                data-testid="live-stop-btn"
                className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2 text-sm"
              >
                <Square className="h-3 w-3 fill-zinc-950" /> Stop &amp; submit
              </button>
            )}
            {uploading && <UploadStages phase={upPhase} pct={upPct} />}
            {done && !uploading && (
              <button
                onClick={finalize}
                disabled={finalizing}
                data-testid="live-finalize-btn"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 text-sm shadow-[0_0_20px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
              >
                {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {finalizing ? "Preparing your report…" : "Generate Executive Report"}
              </button>
            )}
            <label className="ml-auto inline-flex items-center gap-2 text-[11px] text-zinc-500 select-none">
              <input
                type="checkbox"
                checked={enableVideo}
                disabled={recording || uploading}
                onChange={(e) => toggleVideo(e.target.checked)}
                data-testid="live-video-toggle"
                className="accent-amber-500 disabled:opacity-50"
              />
              Camera on
            </label>
          </div>
          {(uploadErr || recErr) && !uploading && (
            <div data-testid="live-recorder-error" className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{uploadErr || recErr}</span>
              </div>
              {failedUpload && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => doUpload(failedUpload)}
                    data-testid="live-retry-upload-btn"
                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 text-xs"
                  >
                    <RefreshCw className="h-3 w-3" /> Retry upload
                  </button>
                  <button
                    onClick={() => { setFailedUpload(null); setUploadErr(""); }}
                    data-testid="live-rerecord-btn"
                    className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-200 hover:bg-zinc-900 rounded-md px-3 py-1.5 text-xs"
                  >
                    Re-record instead
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Progress</div>
            <div className="flex gap-1.5">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full ${i < answeredPrimary ? "bg-amber-500" : "bg-zinc-800"}`}
                />
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-3">
              Wait for the interviewer to finish speaking, then click <b>Start speaking</b>.
            </div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 text-[11px] text-zinc-500">
            Tip: aim for 60–120 seconds per answer using STAR, Situation, Task, Action, Result. No time cap: record as long as you need, then tap Stop.
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Main page -------------------------- */

export default function LiveInterview() {
  const [entitlement, setEntitlement] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("setup");
  const [priorSessions, setPriorSessions] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { isFree, refresh: refreshEntitlement, track } = useEntitlement();
  const { user: authUser } = useAuth();
  const { activeId: wsActiveId, loading: wsLoading } = useWorkspace();
  const isGuestReport = !!authUser?.is_guest;

  useEffect(() => {
    if (wsLoading) return;
    (async () => {
      try {
        const [e, p, list] = await Promise.all([
          api.get("/video-interview/entitlement"),
          api.get("/video-interview/personas"),
          api.get("/video-interview/list").catch(() => ({ data: [] })),
        ]);
        setEntitlement(e.data);
        setPersonas(p.data.personas || []);
        setPriorSessions((list.data || []).filter((s) => (s.mode || "video") === "live" || true));
      } catch {
        setEntitlement({ premium: false });
      }
    })();
  }, [wsLoading, wsActiveId]);

  if (entitlement === null) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm py-16">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  // Freemium: allow free users to run their 1 free session end-to-end. Only
  // gate them out AFTER they've exhausted their allowance - and even then, we
  // show the free summary/upgrade path rather than a hard lock. Paid users
  // and free-with-allowance both continue to the setup screen.
  const canRun = entitlement.is_paid || entitlement.live?.allowed;
  if (!canRun) return <LockedPreview />;

  return (
    <div data-testid="live-page" className="animate-in">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">
            04 · Official AI Interview · {entitlement.is_paid ? "Premium" : "Free · 1st Question Included"}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Official AI Interview</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Put your preparation to the test. Complete the assessment to receive your Official
            Readiness Score. You can start anytime, completing your preparation first gives you
            the best assessment.
          </p>
        </div>
        {phase !== "setup" && session && (
          <button
            onClick={() => { setSession(null); setPhase("setup"); }}
            data-testid="live-back-setup"
            className="hidden sm:inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-200"
          >
            <RefreshCw className="h-3 w-3" /> New session
          </button>
        )}
      </div>

      {phase === "setup" && !session && (
        <>
          <SetupPanel
            personas={personas}
            isPaid={!!entitlement.is_paid}
            onStart={(s) => {
              setSession(s);
              setPhase("record");
              track("live.started", { session_id: s?.session_id, persona: s?.persona });
            }}
          />
          {priorSessions.length > 0 && (
            <div className="mt-10 max-w-2xl">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Recent sessions</div>
              <div className="space-y-2">
                {priorSessions.slice(0, 5).map((s) => (
                  <button
                    key={s.session_id}
                    data-testid={`live-recent-${s.session_id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/video-interview/${s.session_id}`);
                        setSession(data);
                        setPhase(data.status === "completed" ? "report" : "record");
                      } catch { /* noop */ }
                    }}
                    className="w-full text-left p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 flex items-center gap-2 min-w-0">
                        <span className="truncate">{s.job_title || "Session"}</span>
                        {s.is_sample && (
                          <span data-testid={`live-recent-sample-badge-${s.session_id}`} className="text-[9px] uppercase tracking-wider font-semibold text-sky-300 bg-sky-500/10 border border-sky-500/25 rounded px-1.5 py-0.5 flex-shrink-0">Sample</span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {new Date(s.created_at).toLocaleString()} · {s.status}{s.total_questions ? ` · ${s.total_questions} question${s.total_questions === 1 ? "" : "s"}` : ""}
                      </div>
                    </div>
                    {s.overall_score !== null && s.overall_score !== undefined && (
                      <span className="text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-md px-2 py-1 flex-shrink-0">
                        {s.overall_score}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {phase === "record" && session && (
        <LiveRecorder
          session={session}
          onSession={(s) => setSession(s)}
          onFinalize={(s) => {
            setSession(s);
            setPhase("report");
            track("live.completed", {
              session_id: s?.session_id,
              persona: s?.persona,
              is_first: isFree,
            });
          }}
        />
      )}

      {phase === "report" && session && session.is_sample && (
        <SampleShareCard sessionId={session.session_id} />
      )}

      {phase === "report" && session && (
        isGuestReport ? (
          <>
            <FreeSummaryScreen
              report={session.report || {}}
              onContinue={() => window.location.assign("/register")}
            />
            <GuestConversionPanel />
          </>
        ) : isFree ? (
          <FreeSummaryScreen
            report={session.report || {}}
            onContinue={() => window.location.assign("/app/upgrade")}
          />
        ) : (
          <>
            <ReportPanel
              session={session}
              onNew={() => { setSession(null); setPhase("setup"); }}
              onSessionUpdate={(s) => setSession(s)}
            />
            <div className="mt-4">
              <DeliverableFeedback
                deliverable="official_interview"
                label="AI Official Interview"
                entityKey={session.session_id}
                context={{ session_id: session.session_id }}
              />
            </div>
          </>
        )
      )}

      <UpgradeLiveModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onActivated={async () => {
          track("upgrade.purchase_completed", { source: "free_summary" });
          await refreshEntitlement();
          setShowUpgrade(false);
        }}
      />
    </div>
  );
}
